import { FC, Fragment } from 'react'
import {
  SectionArticlesTitle,
} from './styles'
import { RenewalData } from 'src/dtos/renewalData'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { IoMdCheckmark } from 'react-icons/io'
import { isClaimsTriggered } from 'src/utils/renewals'
import { numberToCurrency } from 'src/utils/numberFormat'
import { TbFileCheck } from 'react-icons/tb'

interface FirmSectionProps {
  renewalData?: RenewalData;
  isComplete: boolean;
}

const RenewalQuestions: FC<FirmSectionProps> = ({ renewalData, isComplete }) => {
  const renewalSection = renewalData?.renewalSection
  const claims = renewalData?.renewalSection?.claims

  return (
    <section>
      <header>
        <h4>Renewal Questions</h4>
        {isComplete ? (
          <h6><IoMdCheckmark /> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>QUESTIONS</SectionArticlesTitle>
      <article>
        <strong>
          Since your last application, has any firm member acted as “Of Counsel” or Independent Contract Attorney to another law firm?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasICOCAttorneys)}</span>
        {renewalSection?.hasICOCAttorneys === true ? (
          <span>{renewalSection?.hasICOCAttorneysDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Since your last application, has the Firm or any Firm member filed any lawsuits against clients for fees?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasSueForFeesPolicy)}</span>
        {renewalSection?.hasSueForFeesPolicy === true ? (
          <span>{renewalSection?.hasSueForFeesPolicyDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Since your last application, have any of the firm’s lawyers completed a TLIE sponsored CLE course?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasTlieSponsoredCle)}</span>
      </article>
      <article>
        <strong>
          Has the firm or any member been the subject of a legal malpractice claim in the past 10 years?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasMalPracticeClaims)}</span>
        {renewalSection?.hasMalPracticeClaims === true ? (
          <span><h6>Has the matter been previously reported to TLIE?:</h6> {handleYesNoAnswers(renewalSection?.hasMalPracticeClaimsReported)}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Does any firm member or employee (including non-attorneys) of the firm know, have reason to know, or have any basis to believe that a circumstance, act, error or omission might reasonably be expected to be the basis for a professional liability claim against the firm or any attorney?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasMalPracticeBasis)}</span>
        {renewalSection?.hasMalPracticeBasis === true ? (
          <span><h6>Has the matter been previously reported to TLIE?:</h6> {handleYesNoAnswers(renewalSection?.hasMalPracticeBasisReported)}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Has the Firm or any firm member, including attorneys who have joined the Firm, ever been the subject of a grievance complaint in the past five years?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasGrievanceComplain)}</span>
        {renewalSection?.hasGrievanceComplain === true ? (
          <span><h6>Has the matter been previously reported to TLIE?:</h6> {handleYesNoAnswers(renewalSection?.hasGrievanceComplainReported)}</span>
        ) : null}
        {renewalSection?.hasGrievanceComplainReported === false ? 
          renewalSection?.hasGrievanceComplainFile?.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
          )) : null}
      </article>
      <article>
        <strong>
          Has any firm member ever voluntarily accepted discipline or been refused admission to the bar, reprimanded (privately or publicly), suspended from the practice of law, disbarred, received a probationary license or otherwise disciplined by any disciplinary or licensing authority?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasVoluntarilyAcceptedDiscipline)}</span>
        {renewalSection?.hasVoluntarilyAcceptedDiscipline === true ? (
          <span>{renewalSection?.hasVoluntarilyAcceptedDisciplineDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Would you like a separate quote for standalone cyber coverage from one of our partners? This is optional and separate from your Lawyers' Professional Liability quote.
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.wantsStandaloneCyberQuote)}</span>
        {renewalSection?.wantsStandaloneCyberQuote === true ? (
          <>
            <span><h6>Estimated annual revenue:</h6> {renewalSection?.cyberEstimatedAnnualRevenue != null ? numberToCurrency(renewalSection?.cyberEstimatedAnnualRevenue) : 'N/A'}</span>
            <span><h6>Total number of employees:</h6> {renewalSection?.cyberTotalEmployees ?? 'N/A'}</span>
            <span><h6>Firm website:</h6> {renewalSection?.cyberFirmWebsiteUrl ?? 'N/A'}</span>
          </>
        ) : null}
      </article>
      {(renewalData && isClaimsTriggered(renewalData) === true && claims && claims?.length > 0) ? claims.map((claim, index) => (
        <Fragment key={index + 1}>
          <SectionArticlesTitle>CLAIM {index + 1}</SectionArticlesTitle>
          <article>
            <strong>Firm Name</strong>
            <span>{claim?.firmName ?? 'N/A'}</span>
          </article>
          <article>
            <strong>Individuals of firm involved in the claim</strong>
            {claim?.hasInvolvedMembers === false ? (
              <span>I'm not sure</span>
            ) : claim?.involvedMembers ? claim.involvedMembers.map(member => (
              <span key={member}>{member}</span>
            )) : <span>N/A</span>}
          </article>
          <article>
            <strong>Claimant’s Full Name</strong>
            <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Claim Type</strong>
            <span>{claim?.claimType ?? 'N/A'}</span>
          </article>
          <article>
            <strong>Date of Claim</strong>
            <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Date of Alleged Error</strong>
            <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Is this claim closed?</strong>
            <span>{claim?.isClaimClosed ?? 'N/A'}</span>
            {claim?.isClaimClosed === 'Yes' ? (
              <>
                <span>
                  <h6>Indicate how the claim was closed: </h6>
                  {claim?.closingType ?? 'N/A'}
                </span>
                <span>
                  <h6>Total loss paid including deductibles: </h6>
                  {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
                </span>
                <span>
                  <h6>Defense cost paid: </h6>
                  {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
                </span>
              </>
            ) : null}
          </article>
          <article>
            <strong>Name of Insurer</strong>
            <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Description of Claim</strong>
            <span>
              <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
            </span>
            <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
            <span>
              <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
            </span>
            <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
            <span>
              <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
            </span>
            <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
            <span>
              <h6>Upload any applicable claim information:</h6>
            </span>
            {claim?.claimFile?.map(file => (
              <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
            ))}
          </article>
        </Fragment>
      )) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{renewalSection?.reviewData?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!renewalSection?.reviewData?.files && renewalSection?.reviewData?.files.length > 0 ? 
          renewalSection?.reviewData?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default RenewalQuestions